import React from 'react';
import { Flex, Box, Text, Button, Dialog, DialogTrigger, DialogContent, DialogFooter, TextField, Select } from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import type { UseMutationResult } from '@tanstack/react-query';
import type { ScheduleType, IntervalUnit, JobDefinition } from './types.js';
import {
  intervalSecondsFromUnit,
  scheduleTypeOptions,
  intervalUnitOptions,
} from './types.js';
import { JobConfigSection } from './JobConfigSection.js';
import { PayloadEditor } from './PayloadEditor.js';
import { ErrorBanner } from './ErrorBanner.js';
import { CronPatternPresets, TimezonePresets } from './CronPresetButtons.js';
import { useCronValidation } from './useCronValidation.js';
import { buildCronScheduleConfig } from './cronScheduleConfig.js';

type CreateScheduleInput = {
  jobType: string;
  name: string;
  scheduleType: ScheduleType;
  scheduleConfig: unknown;
  payload: Record<string, unknown>;
  firstRunAt?: string;
  orgId?: string;
};

type CreateScheduleMutation = Pick<
  UseMutationResult<unknown, Error, CreateScheduleInput>,
  'error' | 'isError' | 'isPending' | 'mutate'
>;

type CreateScheduleDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  createMutation: CreateScheduleMutation;
  jobDefinitions?: JobDefinition[] | null;
  isLoadingDefinitions: boolean;
};

const EMPTY_JOB_DEFINITIONS: JobDefinition[] = [];

export function CreateScheduleDialog({
  isOpen,
  onOpenChange,
  createMutation,
  jobDefinitions,
  isLoadingDefinitions,
}: CreateScheduleDialogProps) {
  const normalizedJobDefinitions = React.useMemo(
    () => (Array.isArray(jobDefinitions) ? jobDefinitions : EMPTY_JOB_DEFINITIONS),
    [jobDefinitions]
  );
  const [formName, setFormName] = React.useState('');
  const [formJobType, setFormJobType] = React.useState<string | null>(null);
  const [formScheduleType, setFormScheduleType] = React.useState<ScheduleType>('interval');
  const [formPayload, setFormPayload] = React.useState('{}');
  const [formTargetOrgId, setFormTargetOrgId] = React.useState<string | null>(null);
  const [formRunAt, setFormRunAt] = React.useState('');
  const [formIntervalValue, setFormIntervalValue] = React.useState('5');
  const [formIntervalUnit, setFormIntervalUnit] = React.useState<IntervalUnit>('minutes');
  const [formFirstRunAt, setFormFirstRunAt] = React.useState('');
  const [formEndsAt, setFormEndsAt] = React.useState('');
  const [formCronPattern, setFormCronPattern] = React.useState('0 8 * * 1-5');
  const [formCronTimezone, setFormCronTimezone] = React.useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const isPending = createMutation.isPending;
  const cronValidation = useCronValidation({
    pattern: formCronPattern,
    timezone: formCronTimezone,
    enabled: formScheduleType === 'cron',
  });
  // True while cron submission would fail: no verdict yet for the CURRENT pattern
  // (still debouncing/validating), or the settled verdict is a failure. Without this,
  // the button either shows a false "invalid pattern" message mid-debounce, or — once
  // settled on a failure — stays enabled while every click is a silent no-op (the
  // handler blocks but the error is already shown inline, so it sets nothing new).
  // Disabling submit here makes both cases honest instead of reacting after the fact.
  // An empty pattern is exempt so the "required" message in handleCreate can still fire.
  const cronBlocksSubmit =
    formScheduleType === 'cron' &&
    Boolean(formCronPattern) &&
    (!cronValidation.settled || Boolean(cronValidation.error) || cronValidation.preview.length === 0);

  // Set default job type when definitions load
  React.useEffect(() => {
    if (!formJobType && normalizedJobDefinitions.length) {
      setFormJobType(normalizedJobDefinitions[0]!.jobType);
    }
  }, [normalizedJobDefinitions, formJobType]);

  // Clear target org when switching away from org-scoped job types
  React.useEffect(() => {
    if (formJobType !== 'qa.known-page-analysis') {
      setFormTargetOrgId(null);
    }
  }, [formJobType]);

  // Load payload template when job type changes
  React.useEffect(() => {
    if (formJobType) {
      const definition = normalizedJobDefinitions.find((def) => def.jobType === formJobType);
      const template = definition?.payloadTemplate ?? {};
      setFormPayload(JSON.stringify(template, null, 2));
    }
  }, [formJobType, normalizedJobDefinitions]);

  const resetForm = () => {
    setFormName('');
    setFormJobType(normalizedJobDefinitions[0]?.jobType ?? null);
    setFormScheduleType('interval');
    setFormPayload('{}');
    setFormTargetOrgId(null);
    setFormRunAt('');
    setFormIntervalValue('5');
    setFormIntervalUnit('minutes');
    setFormFirstRunAt('');
    setFormEndsAt('');
    setFormCronPattern('0 8 * * 1-5');
    setFormCronTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setFormError(null);
  };

  const handleCreate = () => {
    setFormError(null);

    if (!formName.trim()) {
      setFormError('Please enter a schedule name');
      return;
    }
    if (!formJobType) {
      setFormError('Please select a job type');
      return;
    }

    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(formPayload);
    } catch {
      setFormError('Invalid JSON payload');
      return;
    }

    const payloadOrgId = typeof payload.orgId === 'string' ? payload.orgId : null;
    const targetOrgId = payloadOrgId ?? formTargetOrgId;

    let scheduleConfig: unknown;
    let firstRunAt: string | undefined;

    if (formScheduleType === 'simple') {
      if (!formRunAt) {
        setFormError('Run date/time is required for simple schedules');
        return;
      }
      const runDate = new Date(formRunAt);
      const time = `${runDate.getHours().toString().padStart(2, '0')}:${runDate.getMinutes().toString().padStart(2, '0')}`;
      scheduleConfig = { time, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    } else if (formScheduleType === 'interval') {
      const intervalValue = parseFloat(formIntervalValue);
      if (!intervalValue || intervalValue <= 0) {
        setFormError('Interval must be a positive number');
        return;
      }
      if (!Number.isFinite(intervalValue)) {
        setFormError('Interval must be a valid number');
        return;
      }

      const intervalSeconds = intervalSecondsFromUnit(intervalValue, formIntervalUnit);
      if (intervalSeconds < 60) {
        setFormError('Interval must be at least 60 seconds (1 minute)');
        return;
      }
      if (intervalSeconds > 30 * 86400) {
        setFormError('Interval cannot exceed 30 days');
        return;
      }

      scheduleConfig = { intervalMs: intervalSeconds * 1000 };
      if (formFirstRunAt) {
        firstRunAt = new Date(formFirstRunAt).toISOString();
      }
    } else if (formScheduleType === 'cron') {
      const result = buildCronScheduleConfig(formCronPattern, formCronTimezone, cronValidation);
      if ('config' in result) {
        scheduleConfig = result.config;
      } else {
        if ('error' in result) setFormError(result.error);
        return;
      }
    }

    // Org-scoped job types require a target org
    let orgId: string | undefined;
    if (formJobType === 'qa.known-page-analysis') {
      if (!targetOrgId) {
        setFormError('Please select an organization for this known page analysis job');
        return;
      }
      orgId = targetOrgId;
    }

    const data: CreateScheduleInput = {
      name: formName.trim(),
      jobType: formJobType,
      scheduleType: formScheduleType,
      scheduleConfig,
      payload,
      ...(firstRunAt && { firstRunAt }),
      ...(orgId && { orgId }),
    };

    createMutation.mutate(data, {
      onSuccess: () => {
        onOpenChange(false);
        resetForm();
      },
    });
  };

  const jobTypeOptions = React.useMemo(
    () => normalizedJobDefinitions.map((def) => ({ id: def.jobType, label: def.jobType })),
    [normalizedJobDefinitions]
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <Button variant="primary" isDisabled={isLoadingDefinitions}>
        <Flex align="center" gap="sm">
          <Plus size={16} />
          Create Schedule
        </Flex>
      </Button>
      <Dialog title="Create Scheduled Job">
        <DialogContent>
          <Flex direction="column" gap="md">
            <TextField
              label="Schedule Name"
              placeholder="e.g., Daily backup job"
              value={formName}
              onChange={setFormName}
              isDisabled={isPending}
            />

            <Select
              label="Job Type"
              placeholder="Select job type"
              items={jobTypeOptions}
              selectedKey={formJobType ?? undefined}
              onSelectionChange={(key) => setFormJobType(String(key))}
              isDisabled={isLoadingDefinitions || isPending}
            />

            <Select
              label="Schedule Type"
              placeholder="Select schedule type"
              items={scheduleTypeOptions}
              selectedKey={formScheduleType}
              onSelectionChange={(key) => setFormScheduleType(key as ScheduleType)}
              isDisabled={isPending}
            />

            {formScheduleType === 'simple' && (
              <TextField
                label="Run At"
                type="datetime-local"
                value={formRunAt}
                onChange={setFormRunAt}
                isDisabled={isPending}
              />
            )}

            {formScheduleType === 'interval' && (
              <>
                <Box>
                  <Flex gap="md" align="end">
                    <Box className="flex-1">
                      <TextField
                        label="Interval"
                        type="number"
                        placeholder="e.g., 5"
                        value={formIntervalValue}
                        onChange={setFormIntervalValue}
                        isDisabled={isPending}
                      />
                    </Box>
                    <Box className="flex-1">
                      <Select
                        label="Unit"
                        items={intervalUnitOptions}
                        selectedKey={formIntervalUnit}
                        onSelectionChange={(key) => setFormIntervalUnit(key as IntervalUnit)}
                        isDisabled={isPending}
                      />
                    </Box>
                  </Flex>
                  <Text size="xs" className="text-muted-foreground mt-1">
                    Min: 1 minute, Max: 30 days
                  </Text>
                </Box>
                <TextField
                  label="First Run At (optional)"
                  type="datetime-local"
                  value={formFirstRunAt}
                  onChange={setFormFirstRunAt}
                  isDisabled={isPending}
                  description="When should the first run occur? If not set, starts immediately."
                />
                <TextField
                  label="Ends At (optional)"
                  type="datetime-local"
                  value={formEndsAt}
                  onChange={setFormEndsAt}
                  isDisabled={isPending}
                />
              </>
            )}

            {formScheduleType === 'cron' && (
              <>
                <Box>
                  <TextField
                    label="Cron Pattern"
                    placeholder="e.g., 0 */6 * * * (every 6 hours)"
                    value={formCronPattern}
                    onChange={setFormCronPattern}
                    isDisabled={isPending}
                    description="Standard cron expression (minute hour day month weekday)"
                  />
                  <CronPatternPresets onSelect={setFormCronPattern} disabled={isPending} />
                </Box>
                <TextField
                  label="Timezone"
                  value={formCronTimezone}
                  onChange={setFormCronTimezone}
                  isDisabled={isPending}
                  description={
                    <>
                      <a
                        href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        IANA timezone
                      </a>
                      {' (e.g., America/New_York, UTC)'}
                    </>
                  }
                />
                <Box>
                  <TimezonePresets onSelect={setFormCronTimezone} disabled={isPending} />
                </Box>
                {cronValidation.validating && (
                  <Box>
                    <Text size="sm" className="text-muted-foreground">
                      Validating pattern...
                    </Text>
                  </Box>
                )}
                {cronValidation.settled && cronValidation.preview.length > 0 && (
                  <Box className="space-y-1">
                    <Text size="xs" weight="medium" className="mb-2 block">
                      Next 5 scheduled runs:
                    </Text>
                    <div className="space-y-1">
                      {cronValidation.preview.map((time, i) => (
                        <Text key={i} size="sm" className="font-mono text-muted-foreground">
                          {new Date(time).toLocaleString()}
                        </Text>
                      ))}
                    </div>
                  </Box>
                )}
                {cronValidation.settled && cronValidation.error && (
                  <ErrorBanner message={cronValidation.error} />
                )}
              </>
            )}

            {formJobType && (
              <JobConfigSection
                jobType={formJobType}
                payloadJson={formPayload}
                onPayloadChange={(payload) => setFormPayload(JSON.stringify(payload, null, 2))}
                onOrgIdChange={setFormTargetOrgId}
                disabled={isPending}
              />
            )}

            <PayloadEditor value={formPayload} onChange={setFormPayload} disabled={isPending} />

            {formError && <ErrorBanner message={formError} />}
            {createMutation.isError && (
              <ErrorBanner
                message={
                  createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Failed to create schedule'
                }
              />
            )}
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button
            variant="outline"
            onPress={() => {
              onOpenChange(false);
              resetForm();
            }}
            isDisabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleCreate}
            isDisabled={isPending || !formJobType || cronBlocksSubmit}
          >
            {isPending ? 'Creating...' : 'Create Schedule'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
